/**
 * detourScore.ts — scoring covoiturage basé sur le VRAI détour conducteur.
 *
 * Contexte (plan Mapbox 2026-07) : l'ancien matching mesurait soit la distance
 * perpendiculaire domicile passager ↔ route (client, `src/lib/mapbox/matching.ts`),
 * soit un overlap de surface (serveur, `carpool-geometric-overlap.ts`). Aucun ne
 * mesurait le **surcoût réel en minutes** pour le conducteur — pourtant c'est le
 * standard BlaBlaCar Daily / Karos (détour ≤ ~8 min, marche passager ≤ ~10 min).
 *
 * Ce module contient UNIQUEMENT de l'arithmétique pure (aucune dépendance Mapbox,
 * turf, Deno ou React) pour être :
 *   - testable en vitest (Node),
 *   - importable côté client (Vite),
 *   - recopié à l'identique côté edge Deno (cf. carpool-geometric-overlap.ts,
 *     qui ré-exporte les mêmes formules — garder les deux synchronisés).
 *
 * Détour = route(driverHome → passenger → driverWork) − route(driverHome → driverWork).
 *
 * @see src/lib/mapbox/matching.ts (géométrie brute, désormais signal secondaire)
 * @see supabase/functions/_shared/carpool-geometric-overlap.ts (overlap + copie Deno)
 */

/** Détour conducteur max accepté (minutes) au-delà duquel le match est rejeté. */
export const MAX_DRIVER_DETOUR_MINUTES = 8;
/** Marche passager max acceptée (minutes) domicile → point de rendez-vous. */
export const MAX_PASSENGER_WALK_MINUTES = 10;
/** Overlap géométrique minimal conservé comme garde-fou secondaire (0..1). */
export const MIN_ROUTE_OVERLAP_PCT = 0.15;

/** Pondérations du score composite (somme = 1). */
export const DETOUR_SCORE_WEIGHTS = {
  detour: 0.45,
  walk: 0.2,
  overlap: 0.25,
  reputation: 0.1,
} as const;

export interface DetourScoreThresholds {
  maxDriverDetourMinutes: number;
  maxPassengerWalkMinutes: number;
  minOverlapPct: number;
}

export const DEFAULT_DETOUR_THRESHOLDS: DetourScoreThresholds = {
  maxDriverDetourMinutes: MAX_DRIVER_DETOUR_MINUTES,
  maxPassengerWalkMinutes: MAX_PASSENGER_WALK_MINUTES,
  minOverlapPct: MIN_ROUTE_OVERLAP_PCT,
};

export interface DetourScoreInput {
  /** Détour conducteur en minutes (≥ 0). */
  detourMinutes: number;
  /** Overlap géométrique route passager/conducteur (0..1). */
  overlapPct: number;
  /** Marche passager domicile → pickup en minutes. Optionnel (constant par recherche). */
  passengerWalkMinutes?: number;
  /** Réputation conducteur 0..100 (fmd_v1a_user_reliability_score). Optionnel. */
  driverReputationScore?: number | null;
}

export interface DetourScoreResult {
  /** Score composite 0..1 (1 = idéal). Sert au tri. */
  score: number;
  /** True si le match passe les garde-fous durs (détour, marche, overlap). */
  accepted: boolean;
  /** Sous-scores exposés pour transparence / debug / dashboard. */
  detourScore: number;
  walkScore: number;
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/**
 * Détour conducteur en minutes = durée route avec passager − durée route directe.
 * Clampé à 0 (une route « avec passager » plus courte que la directe est un
 * artefact Mapbox/trafic, pas un gain réel).
 */
export function computeDetourMinutes(
  baselineDurationS: number,
  viaDurationS: number,
): number {
  return Math.max(0, (viaDurationS - baselineDurationS) / 60);
}

/**
 * Détour conducteur en km = distance route avec passager − distance route directe.
 * Clampé à 0.
 */
export function computeDetourKm(
  baselineDistanceM: number,
  viaDistanceM: number,
): number {
  return Math.max(0, (viaDistanceM - baselineDistanceM) / 1000);
}

/**
 * Calcule le score composite d'un match et s'il passe les garde-fous durs.
 *
 * Garde-fous (tous requis) : détour ≤ max, marche ≤ max (si fournie),
 * overlap ≥ min. Le score composite ne sert qu'au TRI des matches acceptés.
 */
export function computeDetourScore(
  input: DetourScoreInput,
  thresholds: DetourScoreThresholds = DEFAULT_DETOUR_THRESHOLDS,
): DetourScoreResult {
  const detourScore = clamp01(
    1 - input.detourMinutes / thresholds.maxDriverDetourMinutes,
  );

  const walkScore =
    input.passengerWalkMinutes == null
      ? 1
      : clamp01(1 - input.passengerWalkMinutes / thresholds.maxPassengerWalkMinutes);

  const overlap = clamp01(input.overlapPct);

  // Réputation : neutre (0.5) si inconnue → n'avantage ni ne pénalise un user neuf.
  const reputation =
    input.driverReputationScore == null
      ? 0.5
      : clamp01(input.driverReputationScore / 100);

  const score =
    DETOUR_SCORE_WEIGHTS.detour * detourScore +
    DETOUR_SCORE_WEIGHTS.walk * walkScore +
    DETOUR_SCORE_WEIGHTS.overlap * overlap +
    DETOUR_SCORE_WEIGHTS.reputation * reputation;

  const accepted =
    input.detourMinutes <= thresholds.maxDriverDetourMinutes &&
    (input.passengerWalkMinutes == null ||
      input.passengerWalkMinutes <= thresholds.maxPassengerWalkMinutes) &&
    overlap >= thresholds.minOverlapPct;

  return {
    score: clamp01(score),
    accepted,
    detourScore,
    walkScore,
  };
}
